import { layoutCurvedText } from './textCurve';
import type { CurveGlyph } from '@ox/schema';

/** Measure design-text box size in mm (fontSize is already stored in mm). */

export type TextMeasureInput = {
  content: string;
  fontFamily: string;
  fontSize: number;
  fontWeight?: number | string;
  lineHeight?: number;
  curve?: 'none' | 'arc-up' | 'arc-down';
  curveRadius?: number;
};

let measureCtx: CanvasRenderingContext2D | null = null;

function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (measureCtx) return measureCtx;
  const canvas = document.createElement('canvas');
  measureCtx = canvas.getContext('2d');
  return measureCtx;
}

/** Normalize weight so canvas measure + draw use the same CSS font string. */
export function cssFontWeight(fontWeight?: number | string): string {
  if (fontWeight === 'bold' || fontWeight === 'normal' || fontWeight === 'italic') {
    return String(fontWeight);
  }
  const n = Number(fontWeight);
  if (Number.isFinite(n) && n >= 600) return 'bold';
  return 'normal';
}

function charWidthMm(
  ch: string,
  fontFamily: string,
  fontSize: number,
  fontWeight: number | string,
): number {
  const ctx = getMeasureCtx();
  const weight = cssFontWeight(fontWeight);
  if (!ctx) return fontSize * (ch === ' ' ? 0.33 : 0.56);
  ctx.font = `${weight} ${fontSize}px ${fontFamily}`;
  return Math.max(fontSize * 0.05, ctx.measureText(ch).width);
}

/**
 * Bake curved layout in mm — persisted on the text object so canvas/preview/print match.
 */
export function bakeCurveLayoutMm(input: TextMeasureInput & {
  curve: 'arc-up' | 'arc-down';
}): {
  width: number;
  height: number;
  curveRadius: number;
  curveGlyphs: CurveGlyph[];
  pathCx: number;
  pathCy: number;
} {
  const fontSize = Math.max(2, input.fontSize);
  const weight = input.fontWeight ?? 700;
  // Respect the slider value — do not force a larger radius.
  const curveRadius = Math.max(input.curveRadius ?? 80, fontSize * 0.3);
  const layout = layoutCurvedText({
    content: input.content,
    fontSize,
    curve: input.curve,
    curveRadius,
    measureChar: (ch) => charWidthMm(ch, input.fontFamily, fontSize, weight),
  });
  return {
    width: layout.width,
    height: layout.height,
    curveRadius,
    curveGlyphs: layout.glyphs.map((g) => ({
      char: g.char,
      x: g.x,
      y: g.y,
      rotation: g.rotation,
    })),
    pathCx: layout.pathCx,
    pathCy: layout.pathCy,
  };
}

/**
 * Returns a tight bounding box for the text in the same mm units used by the design schema.
 * Uses canvas measureText with fontSize expressed as px so 1 unit ≈ 1 mm.
 */
export function measureTextBoxMm(input: TextMeasureInput): { width: number; height: number } {
  const fontSize = Math.max(2, input.fontSize);
  const lineHeight = input.lineHeight ?? 1.2;
  const weight = input.fontWeight ?? 700;
  const curve = input.curve ?? 'none';

  if (curve === 'arc-up' || curve === 'arc-down') {
    const baked = bakeCurveLayoutMm({ ...input, curve });
    return { width: baked.width, height: baked.height };
  }

  const lines = (input.content || ' ').split('\n');
  const ctx = getMeasureCtx();
  const cssWeight = cssFontWeight(weight);

  let maxW: number;
  if (ctx) {
    ctx.font = `${cssWeight} ${fontSize}px ${input.fontFamily}`;
    maxW = 0;
    for (const line of lines) {
      const sample = line.length ? line : ' ';
      const metrics = ctx.measureText(sample);
      // Prefer ink bounds when available — display fonts (Bangers, etc.) overhang advance width.
      const ink =
        Number.isFinite(metrics.actualBoundingBoxLeft) &&
        Number.isFinite(metrics.actualBoundingBoxRight)
          ? Math.abs(metrics.actualBoundingBoxLeft) + Math.abs(metrics.actualBoundingBoxRight)
          : 0;
      maxW = Math.max(maxW, Math.max(metrics.width, ink));
    }
  } else {
    maxW = Math.max(...lines.map((l) => (l.length || 1) * fontSize * 0.55));
  }

  // Extra pad so Konva Text with wrap=none does not clip the last glyph.
  const width = Math.max(2, maxW + fontSize * 0.2);
  const height = Math.max(fontSize, lines.length * fontSize * lineHeight);

  return { width, height };
}

/**
 * Pick a fontSize so the text spans the print area width (La Tostadora–style default),
 * without exceeding the print area height. Returns size + centered x/y.
 */
export function fitTextToPrintArea(
  input: Omit<TextMeasureInput, 'fontSize'> & { fontSize?: number },
  area: { width: number; height: number },
): { fontSize: number; width: number; height: number; x: number; y: number } {
  const probeSize = 10;
  const probe = measureTextBoxMm({ ...input, fontSize: probeSize });
  const targetW = area.width * 0.98;
  const targetH = area.height * 0.9;
  const scale = Math.min(
    targetW / Math.max(probe.width, 0.01),
    targetH / Math.max(probe.height, 0.01),
  );
  const fontSize = Math.max(4, Math.min(probeSize * scale, area.height));
  const box = measureTextBoxMm({ ...input, fontSize });
  return {
    fontSize,
    width: box.width,
    height: box.height,
    x: (area.width - box.width) / 2,
    y: (area.height - box.height) / 2,
  };
}
